import { Component, OnInit } from '@angular/core';
import { Store } from '../../../store';
import { TwilioService } from '../../../services/twilio.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SubMenuBase } from '../../../models/menu-item';
import { TimePickerTime } from '../../time-picker/time-picker.component';
import { format, parse } from 'date-fns';
import { forkJoin, of } from 'rxjs';
import { UserService } from '../../../services/user.service';
import { AlertService } from 'src/app/services/alert.service';

export type CallSubMenuBase = SubMenuBase & {
    title: string;
    active: boolean;
    id?: any;
    hours: Array<{ from: TimePickerTime; to: TimePickerTime; id?: string }>;
};

@Component({
    selector: 'app-calls-settings',
    templateUrl: './calls-settings.component.html',
    styleUrls: ['./calls-settings.component.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: '0' })),
            transition('void <=> *', [animate('150ms ease-in')]),
        ]),
        trigger('fadeOut', [state('*', style({ opacity: '1' })), transition('* <=> void', [animate('50ms ease-out')])]),
        trigger('fadeInOut', [
            state('void', style({ opacity: '0' })),
            transition('void => *', [animate('150ms ease-out')]),
            state('*', style({ opacity: '1' })),
            transition('* => void', [animate('50ms ease-in')]),
        ]),
    ],
})
export class CallsSettingsComponent implements OnInit {
    isLoading = false;
    playing = null;
    audio = null;

    public weekDaysItems: CallSubMenuBase[] = [
        {
            title: 'Mon',
            active: true,
            id: 1,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Tue',
            active: false,
            id: 2,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Wed',
            active: false,
            id: 3,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Thu',
            active: false,
            id: 4,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Fri',
            active: false,
            id: 5,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Sat',
            active: false,
            id: 6,
            hours: [],
        } as CallSubMenuBase,
        {
            title: 'Sun',
            active: false,
            id: 7,
            hours: [],
        } as CallSubMenuBase,
    ];

    inboundRingtones = [
        {
            name: 'Office phone (default)',
            path: '/assets/inbound_tracks/office_phone.mp3',
        },
        {
            name: 'Positive vibes',
            path: '/assets/inbound_tracks/positive_vibes.mp3',
        },
        {
            name: 'Ringtone pro',
            path: '/assets/inbound_tracks/ringtone_pro.mp3',
        },
        {
            name: 'Simphony',
            path: '/assets/inbound_tracks/simphony.mp3',
        },
        {
            name: 'Simple tone',
            path: '/assets/inbound_tracks/simple_tone.mp3',
        },
        {
            name: 'Sms tone',
            path: '/assets/inbound_tracks/sms_tone.mp3',
        },
    ];
    userInboundRingtone = 0;

    activeWeekDay = 0;

    user = this.store.selectForLocal('user');
    useAlternativePhone = false;
    alternativePhone: any;
    userCallTimeOut = null;
    availableNumbers = null;
    defaultNumber = {};

    userDomainNumbersMap: { [key: string]: { id: string, isPrivate: boolean} } = {};

    constructor(
        private store: Store,
        public twilioService: TwilioService,
        private userService: UserService,
        private alertService: AlertService
    ) {}

    ngOnInit(): void {
        this.useAlternativePhone = !!this.user.contact.altPhone;
        if (this.useAlternativePhone) {
            this.alternativePhone = this.user.contact.altPhone;
        }
        this.userInboundRingtone = this.user.inboundRingtone;
        this.userCallTimeOut = this.user.callTimeOut !== null ? this.user.callTimeOut.toString() : '-1';
        this.twilioService
            .getAvailableHours(this.user.id)
            .subscribe((agentHours) => this.updateAgentAvailabilityHours(agentHours));

        this.twilioService.getUserTwilioNumbers(this.user ? this.user.id : '')
            .subscribe(data => {
                this.availableNumbers = data.reduce((acc, curr) =>
                {
                    if (!acc[curr.domainId]) {
                        acc[curr.domainId] = [];
                    }
                    if (curr.default === '0') {
                        acc[curr.domainId].push(curr);
                    } else {
                        this.defaultNumber[curr.domainId] = curr;
                    }
                    return acc;
                }, {});

                const numberIdMap = data.reduce((acc, curr) => { acc[curr.id] = curr; return acc; }, {});
                this.userDomainNumbersMap = this.user.twNumbers.reduce((acc, { twilioNrId, isPrivate}) => { acc[numberIdMap[twilioNrId].domainId] = { id: twilioNrId, isPrivate: isPrivate === 1 } ; return acc; }, {});
                Object.keys(this.availableNumbers).forEach(domain => {
                    if (!this.userDomainNumbersMap[domain]) {
                        this.userDomainNumbersMap[domain] = { id: '', isPrivate: false };
                    }
                })
            });
    }

    updateAgentAvailabilityHours(agentHours) {
        const dbDayOfWeekMap = agentHours.reduce((acc, item) => {
            if (!acc[item.dayOfWeek]) {
                acc[item.dayOfWeek] = [];
            }
            item.fromMoment = parse(item.stTime, 'HH:mm:ss', new Date());
            item.from = format(item.fromMoment, 'h:m:ss aaa');
            item.toMoment = parse(item.endTime, 'HH:mm:ss', new Date());
            item.to = format(item.toMoment, 'h:m:ss aaa');

            acc[item.dayOfWeek].push(item);
            return acc;
        }, {});

        for (const agentDayHours of this.weekDaysItems) {
            if (dbDayOfWeekMap[agentDayHours.id]) {
                agentDayHours.hours = [];
                dbDayOfWeekMap[agentDayHours.id].forEach((agentHoursDb) => {
                    agentDayHours.hours.push({
                        from: new TimePickerTime({
                            hours: format(agentHoursDb.fromMoment, 'h'),
                            minutes: format(agentHoursDb.fromMoment, 'm'),
                            amPm: format(agentHoursDb.fromMoment, 'aaa') as 'am' | 'pm',
                        }),
                        to: new TimePickerTime({
                            hours: format(agentHoursDb.toMoment, 'h'),
                            minutes: format(agentHoursDb.toMoment, 'm'),
                            amPm: format(agentHoursDb.toMoment, 'aaa') as 'am' | 'pm',
                        }),
                        id: agentHoursDb.id,
                    });
                });
            } else {
                agentDayHours.hours = [];
            }
        }
    }

    toggleDaySelection(event) {
        this.weekDaysItems = this.weekDaysItems.map((item, index) => {
            item.active = event.id === item.id;
            if (event.id === item.id) {
                this.activeWeekDay = index;
            }
            return item;
        });
    }

    addAvailableTimes() {
        this.weekDaysItems[this.activeWeekDay].hours.push({
            from: new TimePickerTime(),
            to: new TimePickerTime(),
        });
    }

    removeAvailableHours(index) {
        this.weekDaysItems[this.activeWeekDay].hours.splice(index, 1);
    }

    saveAvailableHours() {
        this.isLoading = true;
        const validWeekHours = this.weekDaysItems.map((weekDay) => ({
            dayOfWeek: weekDay.id,
            hours: weekDay.hours
                .filter(
                    (item: { from: TimePickerTime; to: TimePickerTime }) =>
                        item.from.hours !== undefined &&
                        item.from.minutes !== undefined &&
                        item.to.hours !== undefined &&
                        item.to.minutes !== undefined
                )
                .map((item: { from: TimePickerTime; to: TimePickerTime; id?: string }) => ({
                    id: item.id,
                    stTime: format(
                        parse(
                            item.from.hours + ':' + item.from.minutes + ':00 ' + item.from.amPm,
                            'h:m:ss a',
                            new Date()
                        ),
                        'HH:mm:ss'
                    ),
                    endTime: format(
                        parse(item.to.hours + ':' + item.to.minutes + ':00 ' + item.to.amPm, 'h:m:ss a', new Date()),
                        'HH:mm:ss'
                    ),
                })),
        }));

        const subs = {
            agentAvailHours: this.twilioService.updateAvailableHours(validWeekHours, this.user.id),
            altPhone: of<any>(null),
            callTimeout: of<any>(null),
            inboundRingtone: of<any>(null),
            userTwilioNumbers: of<any>(null)
        };

        const updateUser: any = {};

        if (this.alternativePhone.e164Number !== this.user.contact.altPhone || (!!this.user.contact.altPhone !== this.useAlternativePhone)) {
            subs.altPhone = this.userService.updateUserContactField('altPhone', this.useAlternativePhone ? this.alternativePhone.e164Number : null);
            updateUser.contact = { altPhone: this.useAlternativePhone ? this.alternativePhone.e164Number : null };
        }

        if (
            +this.userCallTimeOut !== +(this.user.callTimeOut ?? -1) ||
            (this.userCallTimeOut === '-1' && this.user.callTimeOut !== null)
        ) {
            updateUser.callTimeOut = this.userCallTimeOut === '-1' ? null : this.userCallTimeOut;
            subs.callTimeout = this.userService.updateUserField('callTimeOut', updateUser.callTimeOut);
        }

        if (this.userInboundRingtone !== this.user.inboundRingtone) {
            updateUser.inboundRingtone = this.userInboundRingtone;
            subs.inboundRingtone = this.userService.updateUserField('inboundRingtone', this.userInboundRingtone);
        }

        const twNumbers = Object.values(this.userDomainNumbersMap).reduce((acc, curr: any) => { acc[curr.id] = curr; return acc; }, {});
        const isTwNumbersTheSame = this.user.twNumbers.every(item => {
            const isPrivate = item.isPrivate === 1;
            return twNumbers[item.twilioNrId] && +item.twilioNrId > 0 && twNumbers[item.twilioNrId].isPrivate === isPrivate;
        });
        const newUserNumbers = Object.values(this.userDomainNumbersMap).filter(item => item.id);

        if (!isTwNumbersTheSame || this.user.twNumbers.length !== newUserNumbers.length) {
            subs.userTwilioNumbers = this.twilioService.updateUserNumbers(newUserNumbers, this.user.id);
        }

        forkJoin(subs).subscribe(({ agentAvailHours, userTwilioNumbers }: any) => {
            if (Object.keys(updateUser).length > 0) {
                this.store.setProp('user', updateUser);
                this.user = this.store.selectForLocal('user');
            }
            if (agentAvailHours) {
                this.updateAgentAvailabilityHours(agentAvailHours);
            }
            if (userTwilioNumbers) {
                this.user.twNumbers = userTwilioNumbers;
            }
            this.isLoading = false;
            this.alertService.notification(['Settings where updated'], 3000);
        });
    }

    initPlaybackRecord(event, ringtone: { name: string; path: string }) {
        event.stopPropagation();
        event.preventDefault();
        if (this.audio) {
            this.audio.pause();
        } else {
            this.audio = document.createElement('audio');
            this.audio.setAttribute('preload', 'auto');
            this.audio.setAttribute('controls', 'none');
            this.audio.style.display = 'none';
            document.body.appendChild(this.audio);
        }

        this.audio.src = ringtone.path;
        this.audio.play();
        this.playing = ringtone.name;
    }

    stopPlayback(event?) {
        event?.stopPropagation();
        event?.preventDefault();
        this.audio?.pause();
        this.playing = null;
    }

    get availableNumbersKeys()
    {
        return Object.keys(this.availableNumbers);
    }
}
